import { useState, useEffect } from 'react';
import { FontStyle } from '../store/auth.store';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  font: FontStyle;
  onSave: (font: FontStyle) => void;
}

const FONTS = [
  'Roboto Condensed', 'Roboto', 'Poppins', 'Montserrat',
  'Open Sans', 'Lato', 'Oswald', 'Raleway',
  'Nunito', 'Ubuntu', 'Playfair Display', 'Bebas Neue',
];

export default function FontEditorModal({ isOpen, onClose, font, onSave }: Props) {
  const [local, setLocal] = useState<FontStyle>(font);

  useEffect(() => {
    if (isOpen) setLocal({ ...font });
  }, [isOpen, font]);

  if (!isOpen) return null;

  const set = (key: keyof FontStyle, value: any) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const previewStyle: React.CSSProperties = {
    fontFamily: local.fontFamily,
    fontSize: Math.min(local.fontSize, 60),
    color: local.color,
    fontWeight: local.bold ? 'bold' : 'normal',
    fontStyle: local.italic ? 'italic' : 'normal',
    textDecoration: local.underline ? 'underline' : 'none',
    textTransform: local.textTransform === 'none' ? 'none' : local.textTransform,
    letterSpacing: local.letterSpacing + 'px',
    wordSpacing: local.wordSpacing + 'px',
    textAlign: local.textAlign as any,
    textShadow: local.textShadow ? `${local.shadowColor} ${local.shadowOffsetX}px ${local.shadowOffsetY}px ${local.shadowBlur}px` : 'none',
    WebkitTextStroke: local.outline ? `${local.outlineWidth}px ${local.outlineColor}` : undefined,
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Font Style Editor</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Preview */}
        <div className="font-preview">
          <div style={{
            ...previewStyle,
            fontSize: Math.min(local.fontSize, 48),
            lineHeight: 1.3,
          }}>
            Это текст превью!
          </div>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <div className="modal-tab active">Font Settings</div>
        </div>

        <div className="modal-body">
          {/* Font Family */}
          <div className="modal-row">
            <label>Font:</label>
            <select value={local.fontFamily} onChange={(e) => set('fontFamily', e.target.value)}>
              {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Font Size */}
          <div className="modal-row">
            <label>Font Size:</label>
            <div className="range-with-input">
              <input type="range" min="8" max="120" value={local.fontSize} onChange={(e) => set('fontSize', Number(e.target.value))} />
              <input type="number" min="8" max="120" value={local.fontSize} onChange={(e) => set('fontSize', Number(e.target.value))} className="range-value" />
              <span className="unit">px</span>
            </div>
          </div>

          {/* Color */}
          <div className="modal-row">
            <label>Color:</label>
            <div className="color-input">
              <input type="color" value={local.color} onChange={(e) => set('color', e.target.value)} />
              <input type="text" value={local.color} onChange={(e) => set('color', e.target.value)} />
            </div>
          </div>

          {/* Toggles */}
          {[
            { label: 'Bold', key: 'bold' as const },
            { label: 'Italic', key: 'italic' as const },
            { label: 'Underline', key: 'underline' as const },
          ].map(({ label, key }) => (
            <div className="modal-row" key={key}>
              <label>{label}:</label>
              <div className="toggle-group">
                <span className={local[key] ? '' : 'active-text'}>No</span>
                <label className="mini-toggle">
                  <input
                    type="checkbox"
                    checked={local[key]}
                    onChange={(e) => set(key, e.target.checked)}
                  />
                  <span className="mini-track"><span className="mini-thumb" /></span>
                </label>
                <span className={local[key] ? 'active-text' : ''}>Yes</span>
              </div>
            </div>
          ))}

          {/* Text Transform */}
          <div className="modal-row">
            <label>Transform:</label>
            <select value={local.textTransform} onChange={(e) => set('textTransform', e.target.value)}>
              <option value="none">None</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="capitalize">Capitalize</option>
            </select>
          </div>

          {/* Letter Spacing */}
          <div className="modal-row">
            <label>Letter Spacing:</label>
            <div className="range-with-input">
              <input type="range" min="-5" max="20" value={local.letterSpacing} onChange={(e) => set('letterSpacing', Number(e.target.value))} />
              <input type="number" min="-5" max="20" value={local.letterSpacing} onChange={(e) => set('letterSpacing', Number(e.target.value))} className="range-value" />
              <span className="unit">px</span>
            </div>
          </div>

          {/* Word Spacing */}
          <div className="modal-row">
            <label>Word Spacing:</label>
            <div className="range-with-input">
              <input type="range" min="-10" max="40" value={local.wordSpacing} onChange={(e) => set('wordSpacing', Number(e.target.value))} />
              <input type="number" min="-10" max="40" value={local.wordSpacing} onChange={(e) => set('wordSpacing', Number(e.target.value))} className="range-value" />
              <span className="unit">px</span>
            </div>
          </div>

          {/* Alignment */}
          <div className="modal-row">
            <label>Horizontal Align:</label>
            <select value={local.textAlign} onChange={(e) => set('textAlign', e.target.value as any)}>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div className="modal-row">
            <label>Vertical Align:</label>
            <select value={local.verticalAlign} onChange={(e) => set('verticalAlign', e.target.value as any)}>
              <option value="top">Top</option>
              <option value="center">Center</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>

          {/* Shadow */}
          <div className="modal-divider" />
          <h3 style={{ color: '#a1a1aa', fontSize: 14, marginBottom: 16 }}>Shadow</h3>

          <div className="modal-row">
            <label>Shadow:</label>
            <div className="toggle-group">
              <span className={!local.textShadow ? 'active-text' : ''}>No</span>
              <label className="mini-toggle">
                <input type="checkbox" checked={local.textShadow} onChange={(e) => set('textShadow', e.target.checked)} />
                <span className="mini-track"><span className="mini-thumb" /></span>
              </label>
              <span className={local.textShadow ? 'active-text' : ''}>Yes</span>
            </div>
          </div>

          {local.textShadow && (
            <>
              <div className="modal-row">
                <label>Shadow Color:</label>
                <div className="color-input">
                  <input type="color" value={local.shadowColor} onChange={(e) => set('shadowColor', e.target.value)} />
                  <input type="text" value={local.shadowColor} onChange={(e) => set('shadowColor', e.target.value)} />
                </div>
              </div>
              <div className="modal-row">
                <label>Blur:</label>
                <div className="range-with-input">
                  <input type="range" min="0" max="20" value={local.shadowBlur} onChange={(e) => set('shadowBlur', Number(e.target.value))} />
                  <input type="number" min="0" max="20" value={local.shadowBlur} onChange={(e) => set('shadowBlur', Number(e.target.value))} className="range-value" />
                  <span className="unit">px</span>
                </div>
              </div>
              <div className="modal-row">
                <label>Offset X:</label>
                <div className="range-with-input">
                  <input type="range" min="-10" max="10" value={local.shadowOffsetX} onChange={(e) => set('shadowOffsetX', Number(e.target.value))} />
                  <input type="number" min="-10" max="10" value={local.shadowOffsetX} onChange={(e) => set('shadowOffsetX', Number(e.target.value))} className="range-value" />
                  <span className="unit">px</span>
                </div>
              </div>
              <div className="modal-row">
                <label>Offset Y:</label>
                <div className="range-with-input">
                  <input type="range" min="-10" max="10" value={local.shadowOffsetY} onChange={(e) => set('shadowOffsetY', Number(e.target.value))} />
                  <input type="number" min="-10" max="10" value={local.shadowOffsetY} onChange={(e) => set('shadowOffsetY', Number(e.target.value))} className="range-value" />
                  <span className="unit">px</span>
                </div>
              </div>
            </>
          )}

          {/* Outline */}
          <div className="modal-divider" />
          <h3 style={{ color: '#a1a1aa', fontSize: 14, marginBottom: 16 }}>Outline</h3>

          <div className="modal-row">
            <label>Outline:</label>
            <div className="toggle-group">
              <span className={!local.outline ? 'active-text' : ''}>No</span>
              <label className="mini-toggle">
                <input type="checkbox" checked={local.outline} onChange={(e) => set('outline', e.target.checked)} />
                <span className="mini-track"><span className="mini-thumb" /></span>
              </label>
              <span className={local.outline ? 'active-text' : ''}>Yes</span>
            </div>
          </div>

          {local.outline && (
            <>
              <div className="modal-row">
                <label>Color:</label>
                <div className="color-input">
                  <input type="color" value={local.outlineColor} onChange={(e) => set('outlineColor', e.target.value)} />
                  <input type="text" value={local.outlineColor} onChange={(e) => set('outlineColor', e.target.value)} />
                </div>
              </div>
              <div className="modal-row">
                <label>Width:</label>
                <div className="range-with-input">
                  <input type="range" min="1" max="5" value={local.outlineWidth} onChange={(e) => set('outlineWidth', Number(e.target.value))} />
                  <input type="number" min="1" max="5" value={local.outlineWidth} onChange={(e) => set('outlineWidth', Number(e.target.value))} className="range-value" />
                  <span className="unit">px</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-apply" onClick={() => { onSave(local); onClose(); }}>✓ Apply</button>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
        </div>

        <style>{styles}</style>
      </div>
    </div>
  );
}

const styles = `
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }
  .modal-content {
    background: #27272a;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #3f3f46;
  }
  .modal-header h2 { font-size: 18px; color: #e4e4e7; }
  .modal-close {
    background: transparent;
    border: none;
    color: #71717a;
    font-size: 20px;
    cursor: pointer;
    padding: 4px;
  }
  .modal-close:hover { color: #e4e4e7; }
  
  .font-preview {
    padding: 24px;
    background: repeating-conic-gradient(#3f3f46 0% 25%, transparent 0% 50%) 50% / 16px 16px;
    text-align: center;
    border-bottom: 1px solid #3f3f46;
  }
  
  .modal-tabs { display: flex; border-bottom: 1px solid #3f3f46; }
  .modal-tab {
    padding: 12px 20px;
    font-size: 13px;
    color: #71717a;
    border-bottom: 2px solid transparent;
    cursor: pointer;
  }
  .modal-tab.active { color: #f59e0b; border-bottom-color: #f59e0b; }
  
  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }
  .modal-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }
  .modal-row label {
    min-width: 120px;
    font-size: 13px;
    color: #a1a1aa;
  }
  .modal-row select,
  .modal-row input[type="text"],
  .modal-row input[type="number"] {
    flex: 1;
    padding: 8px 12px;
    background: #18181b;
    border: 1px solid #3f3f46;
    border-radius: 6px;
    color: #e4e4e7;
    font-size: 14px;
    outline: none;
  }
  .modal-row select:focus,
  .modal-row input:focus { border-color: #f59e0b; }
  
  .range-with-input {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }
  .range-with-input input[type="range"] { flex: 1; accent-color: #f59e0b; }
  .range-value { width: 60px !important; text-align: center; }
  .unit { font-size: 12px; color: #52525b; min-width: 24px; }
  
  .color-input { display: flex; gap: 8px; align-items: center; flex: 1; }
  .color-input input[type="color"] { width: 40px; height: 36px; border: none; border-radius: 6px; cursor: pointer; background: none; }
  .color-input input[type="text"] { flex: 1; }
  
  .toggle-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .active-text { color: #f59e0b; font-weight: 600; }
  .mini-toggle { display: flex; align-items: center; }
  .mini-toggle input { display: none; }
  .mini-track {
    width: 40px; height: 22px;
    background: #3f3f46;
    border-radius: 11px;
    position: relative;
    cursor: pointer;
    display: block;
  }
  .mini-thumb {
    width: 18px; height: 18px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 2px; left: 2px;
    transition: transform 0.2s;
  }
  .mini-toggle input:checked + .mini-track { background: #f59e0b; }
  .mini-toggle input:checked + .mini-track .mini-thumb { transform: translateX(18px); }
  
  .modal-divider {
    height: 1px;
    background: #3f3f46;
    margin: 20px 0;
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #3f3f46;
  }
  .btn-apply {
    padding: 10px 24px;
    background: #f59e0b;
    border: none;
    border-radius: 20px;
    color: white;
    font-weight: 600;
    font-size: 14px;
  }
  .btn-apply:hover { background: #d97706; }
  .btn-cancel {
    padding: 10px 24px;
    background: transparent;
    border: 1px solid #3f3f46;
    border-radius: 20px;
    color: #a1a1aa;
    font-size: 14px;
  }
  .btn-cancel:hover { border-color: #71717a; color: #e4e4e7; }
`;
